import { AddAccessTokenCache } from "@/helper/cacheHelper";
import {
  AccessToken,
  GetKeepLogin,
  GetTimezoneOffset,
  MerchantId,
  RemoveCookiesToken,
  SetCookiesAccessToken,
  SetCookiesRefreshToken,
} from "@/helper/userHelper";
import { i18n } from "@/i18n";
import { ClearAllCacheFromIndexedDB } from "@/IndexedDB/cacheIndexedDB";
import API from "@/service/Index";
import axios, { AxiosInstance } from "axios";

let isRefreshing = false;
let failedQueue = [] as any;
const MAX_RETRY_COUNT = 10;

const ProcessQueue = () => {
  const token = AccessToken();
  if (token && failedQueue.length > 0) {
    failedQueue.forEach((prom: any) => {
      prom.resolve();
    });
  }
  failedQueue = [];
};

const ReSendApiByNewAccessToken = (instance : AxiosInstance, originalConfig: any) => {
  const token = AccessToken();
  originalConfig.headers["Authorization"] = "Bearer " + token;
   return instance(originalConfig);
};

const CreateAxiosInstance = (baseURL: string) => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json;charset=utf-8",
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const token = AccessToken();
      const merchantId = MerchantId();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      if (merchantId) {
        config.headers["merchantId"] = `${merchantId}`;
      }
      config.headers["language"] = i18n.locale;
      config.headers["Cache-Control"] = "no-store, no-cache, max-age=0, must-revalidate, proxy-revalidate";
      config.headers["CDN-Cache-Control"] = "no-store, no-cache, max-age=0, must-revalidate, proxy-revalidate";
      config.headers["timezoneOffset"] = GetTimezoneOffset();

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  instance.interceptors.response.use(
    (res) => {
      return res;
    },
    (err) => {
      const originalConfig = err.config;
      if (err.response && err.response.status === 401) {
        // Access Token was expire
        if (isRefreshing && !originalConfig._isLeaderRetry) {
          return new Promise((resolve) => {
            failedQueue.push({ resolve });
          }).then(() => {
            return ReSendApiByNewAccessToken(instance, originalConfig);
          });
        }

        if (originalConfig._retryCount && originalConfig._retryCount >= MAX_RETRY_COUNT) {
          location.href = `${window.location.origin}/SessionExpire`;
          return Promise.reject(err);
        }
        originalConfig._retryCount = (originalConfig._retryCount || 0) + 1;

        isRefreshing = true;

        return API.AuthenService.DoGetNewAccessTokenByRefreshToken()
          .then(async (resNewAccessTokenByRefreshToken) => {
            if (resNewAccessTokenByRefreshToken.status.IsSuccess()) {
              const keepLogin = GetKeepLogin();
              await ClearAllCacheFromIndexedDB();
              await AddAccessTokenCache(resNewAccessTokenByRefreshToken.data.access_token);
              SetCookiesAccessToken(resNewAccessTokenByRefreshToken.data.access_token);
              SetCookiesRefreshToken(resNewAccessTokenByRefreshToken.data.refresh_token, keepLogin);
              
               originalConfig._isLeaderRetry = true;

               return ReSendApiByNewAccessToken(instance, originalConfig)
                .then((res) => {
                  isRefreshing = false;
                  ProcessQueue();
                  return res;
                })
                .catch((err) => {
                  isRefreshing = false;
                  ProcessQueue();
                  return Promise.reject(err);
                });
            } else {
              location.href = `${window.location.origin}/SessionExpire`;
            }
          })
          .catch((_error) => {
            RemoveCookiesToken();
            location.href = `${window.location.origin}/SessionExpire`;
            isRefreshing = false;
             ProcessQueue();
          });
      }

      return Promise.reject(err);
    }
  );
  return instance;
};

const peakExportAPI = CreateAxiosInstance(process.env.VUE_APP_PeakExportService || "");
const peakRealTimeAPIForUser = CreateAxiosInstance(process.env.VUE_APP_PeakRealTimeServiceForUser || "");
const peakRealTimeAPI = CreateAxiosInstance(process.env.VUE_APP_PeakRealTimeService || "");
const peakRealTimeAPIForImport = CreateAxiosInstance(process.env.VUE_APP_PeakRealTimeServiceForImport || "");
const peakPayrollAPI = CreateAxiosInstance(process.env.VUE_APP_PeakPayrollService || "");
const peakTaxAPI = CreateAxiosInstance(process.env.VUE_APP_PeakTaxService || "");
const peakAppConnect = CreateAxiosInstance(process.env.VUE_APP_PeakAppConnectService || "");
const peakAuthen = CreateAxiosInstance(process.env.VUE_APP_PeakAuthenService || "");
const peakLogAPI = CreateAxiosInstance(process.env.VUE_APP_PeakLogService || "");
const peakFileService = CreateAxiosInstance(process.env.VUE_APP_PeakFileService || "");
const peakOnlineView = CreateAxiosInstance(process.env.VUE_APP_PeakOnlineViewService || "");
const peakAuthenticationAPIForWithRecheckToken = CreateAxiosInstance(process.env.VUE_APP_PeakAuthenService || "");

export default {
  peakExportAPI,
  peakRealTimeAPI,
  peakRealTimeAPIForUser,
  peakRealTimeAPIForImport,
  peakPayrollAPI,
  peakTaxAPI,
  peakAuthen,
  peakAppConnect,
  peakLogAPI,
  peakFileService,
  peakOnlineView,
  peakAuthenticationAPIForWithRecheckToken,
};
